import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
  Wallet,
  Save,
  CheckCircle,
  Crown,
  Clock,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { updateProfile } from '../services/api';
import { SERVICES } from '../data/services';
import { toast } from 'sonner';

const Profile = () => {
  const navigate = useNavigate();
  const { user, refreshUser, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    city: '',
    pincode: '',
    bio: '',
    hourly_rate: 1000,
    gender: '',
    age: '',
    services: []
  });

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        city: user.city || '',
        pincode: user.pincode || '',
        bio: user.bio || '',
        hourly_rate: user.hourly_rate || 1000,
        gender: user.gender || '',
        age: user.age || '',
        services: user.services || []
      });
    }
  }, [user, token, navigate]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateProfile({
        ...formData,
        hourly_rate: parseInt(formData.hourly_rate) || 1000,
        age: formData.age ? parseInt(formData.age) : null
      });
      await refreshUser();
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b-2 border-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>
            <span className="font-bold">Edit Profile</span>
            <Button
              onClick={handleSave}
              disabled={loading}
              className="neo-button-primary py-2 px-4"
              data-testid="save-profile-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Profile Header */}
        <div className="neo-card p-6 mb-8">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 bg-gradient-to-br from-[#7C3AED] to-[#DB2777] rounded-2xl flex items-center justify-center text-white text-4xl font-black border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-black">{user.name}</h1>
              <p className="text-slate-500 flex items-center gap-2 mt-1">
                <Phone className="w-4 h-4" />
                +91 {user.mobile}
              </p>
              <div className="flex items-center gap-3 mt-3">
                {user.provider_active ? (
                  <span className="badge-success flex items-center gap-1">
                    <Crown className="w-4 h-4" />
                    Provider Active
                  </span>
                ) : (
                  <span className="badge-warning flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Hirer Only
                  </span>
                )}
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  user.current_role === 'provider' 
                    ? 'bg-[#DB2777] text-white' 
                    : 'bg-[#7C3AED] text-white'
                }`}>
                  {user.current_role === 'provider' ? 'Provider Mode' : 'Hirer Mode'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Membership Status */}
        {user.provider_active && (
          <div className="earnings-card mb-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Membership</p>
                <p className="text-2xl font-black">{user.membership?.replace('_', ' ').toUpperCase()}</p>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-sm">Total Earnings</p>
                <p className="text-2xl font-black">₹{user.total_earnings || 0}</p>
              </div>
            </div>
          </div>
        )}

        {/* Basic Info */}
        <div className="neo-card p-6 mb-8">
          <h2 className="text-lg font-bold mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-[#7C3AED]" />
            Basic Information
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="neo-input mt-2"
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="neo-input mt-2"
                placeholder="your@email.com"
                data-testid="profile-email-input"
              />
            </div>
            <div>
              <Label htmlFor="gender">Gender</Label>
              <select
                id="gender"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="neo-input mt-2 w-full"
                data-testid="profile-gender-select"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label htmlFor="age">Age</Label>
              <Input
                id="age"
                type="number"
                min="18"
                max="80"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                className="neo-input mt-2"
                placeholder="25"
                data-testid="profile-age-input"
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="neo-card p-6 mb-8">
          <h2 className="text-lg font-bold mb-6 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#7C3AED]" />
            Location
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="neo-input mt-2"
                placeholder="Mumbai"
                data-testid="profile-city-input"
              />
            </div>
            <div>
              <Label htmlFor="pincode">Pincode</Label>
              <Input
                id="pincode"
                type="text"
                maxLength={6}
                value={formData.pincode}
                onChange={(e) => setFormData({ ...formData, pincode: e.target.value.replace(/\D/g, '') })}
                className="neo-input mt-2"
                placeholder="400001"
                data-testid="profile-pincode-input"
              />
            </div>
          </div>
        </div>

        {/* Provider Settings */}
        {user.provider_active && (
          <>
            <div className="neo-card p-6 mb-8">
              <h2 className="text-lg font-bold mb-6 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#7C3AED]" />
                Provider Settings
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="hourly_rate">Hourly Rate (₹)</Label>
                  <Input
                    id="hourly_rate"
                    type="number"
                    min="100"
                    max="10000"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                    className="neo-input mt-2"
                    data-testid="profile-rate-input"
                  />
                  <p className="text-sm text-slate-500 mt-1">You'll receive 80% (Rs.{Math.round(formData.hourly_rate * 0.8)}/hr). 18% GST is added to the hirer's total.</p>
                </div>
              </div>
              
              <div className="mt-6">
                <Label htmlFor="bio">About You</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="neo-input mt-2"
                  rows={4}
                  placeholder="Tell hirers about yourself, your experience, and what makes you a great partner..."
                  data-testid="profile-bio-input"
                />
              </div>
            </div>

            {/* Services Selection */}
            <div className="neo-card p-6 mb-8">
              <h2 className="text-lg font-bold mb-6 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#7C3AED]" />
                Services You Offer
              </h2>
              <p className="text-slate-500 mb-6">Select the services you want to provide</p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {SERVICES.map((service) => {
                  const Icon = service.icon;
                  const isSelected = formData.services.includes(service.id);
                  return (
                    <div
                      key={service.id}
                      onClick={() => toggleService(service.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-3 ${
                        isSelected 
                          ? 'border-[#7C3AED] bg-[#7C3AED]/10' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`service-toggle-${service.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isSelected ? service.color : 'bg-slate-100'
                      }`}>
                        <Icon className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                      </div>
                      <span className={`text-sm font-medium ${isSelected ? 'text-[#7C3AED]' : 'text-slate-600'}`}>
                        {service.name}
                      </span>
                      {isSelected && (
                        <CheckCircle className="w-4 h-4 text-[#7C3AED] ml-auto" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Become Provider CTA */}
        {!user.provider_active && (
          <div className="neo-card p-6 bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold mb-2">Want to Earn Money?</h3>
                <p className="text-white/80">Become a provider and start offering your services</p>
              </div>
              <Button
                onClick={() => navigate('/membership')}
                className="bg-white text-[#7C3AED] hover:bg-white/90 font-bold"
                data-testid="become-provider-profile-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Get Membership
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
